import { useState } from "react"
import { observer } from "mobx-react-lite"
import { useLocation, useNavigate } from "react-router-dom"

import { cn } from "@/domains/library"

/**
 * Shared type for complex breadcrumb tabs.
 * All complex breadcrumb applications should use this type.
 */
export type ComplexTab = {
  id: string
  label: string | React.ReactNode
  tabLabel: string
  slug: string
  description: string
  icon?: React.ElementType
}

/**
 * Navigation mode for complex breadcrumb dropdowns.
 * - "slug": Replace the last part of the URL path (e.g., /lock/123/increase-amount -> /lock/123/extend-duration)
 * - "path": Navigate to the full path (e.g., /rewards -> /rewards/fees)
 */
export type NavigationMode = "slug" | "path"

export interface ComplexBreadcrumbDropdownProps {
  tabs: ComplexTab[]
  navigationMode?: NavigationMode
  invalidTabMessage?: string
  currentTabOverride?: ComplexTab
  onTabSelectOverride?: (tab: ComplexTab) => void
}

/**
 * Reusable dropdown component for complex breadcrumb applications.
 * Handles tab selection, navigation, and icon rendering.
 */
export const ComplexBreadcrumbDropdown = observer(
  ({
    tabs,
    navigationMode = "slug",
    invalidTabMessage = "Invalid tab",
    currentTabOverride,
    onTabSelectOverride,
  }: ComplexBreadcrumbDropdownProps) => {
    const location = useLocation()
    const navigate = useNavigate()
    const [isOpen, setIsOpen] = useState(false)

    // Find current tab based on navigation mode or use override
    const currentTab =
      currentTabOverride ??
      (navigationMode === "slug"
        ? (findCurrentSlugTab(tabs, location.pathname) ?? tabs[0])
        : (tabs.find((tab) => location.pathname === tab.slug) ?? tabs[0]))

    // Handle tab selection based on navigation mode or use override
    const handleTabSelect = (tab: ComplexTab) => {
      if (onTabSelectOverride) {
        onTabSelectOverride(tab)
      } else if (navigationMode === "slug") {
        const basePath = removeCurrentSlug(location.pathname, tabs)
        navigate(`${basePath}${tab.slug}${location.search}${location.hash}`)
      } else {
        // Navigate to full path
        navigate(tab.slug)
      }
      setIsOpen(false)
    }

    // Check if a tab is currently active
    const isTabActive = (tab: ComplexTab) => {
      if (currentTabOverride) {
        return tab.id === currentTabOverride.id
      }
      if (navigationMode === "slug") {
        return findCurrentSlugTab(tabs, location.pathname)?.id === tab.id
      }
      return location.pathname === tab.slug || location.pathname.endsWith(tab.slug)
    }

    // Ensure currentTab is always defined
    if (!currentTab) {
      return <div>{invalidTabMessage}</div>
    }

    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            "flex cursor-pointer items-center gap-2 whitespace-nowrap rounded px-2 py-1 outline-none ring-0",
          )}
        >
          {currentTab.icon && <currentTab.icon className="-mr-0.5 text-t100" size={13} />}
          <span className="flex items-center justify-start gap-2">{currentTab.label}</span>
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <div
            className={cn(
              "absolute left-0 top-full z-50 mt-1 min-w-[200px]",
              "overflow-hidden rounded-md py-0",
              "border border-p400-light bg-p400",
            )}
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabSelect(tab)}
                className={cn(
                  "w-full cursor-pointer px-4 py-3",
                  "flex items-center justify-start gap-2",
                  isTabActive(tab) ? "bg-p400 text-t100" : "whitespace-nowrap hover:bg-p400-light hover:text-t100",
                )}
              >
                {tab.icon && <tab.icon className="text-t100" size={16} />}
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {isOpen && <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />}
      </div>
    )
  },
)

function findCurrentSlugTab(tabs: Array<ComplexTab>, pathname: string): ComplexTab | undefined {
  const nonEmptySlugMatch = [...tabs]
    .filter((tab) => tab.slug.length > 0)
    .sort((a, b) => b.slug.length - a.slug.length)
    .find((tab) => pathname.endsWith(tab.slug))

  if (nonEmptySlugMatch) return nonEmptySlugMatch
  return tabs.find((tab) => tab.slug.length === 0)
}

function removeCurrentSlug(pathname: string, tabs: Array<ComplexTab>): string {
  const currentSlug = findCurrentSlugTab(tabs, pathname)?.slug
  if (!currentSlug) return pathname
  if (!pathname.endsWith(currentSlug)) return pathname
  return pathname.slice(0, -currentSlug.length)
}
